import React, { useState, useEffect } from 'react'
import { isValidBtw } from '../utils/btw'
import { useAuth } from '../contexts/AuthContext'
import { billingAPI, stripeConnectAPI, invoicesAPI } from '../services/api'
import {
  Receipt,
  Building2,
  CreditCard,
  Save,
  AlertCircle,
  CheckCircle,
  Info,
  RefreshCw,
  ExternalLink,
  Banknote,
  FileText,
  Calculator,
  ShieldCheck,
  Clock,
  Zap,
  ArrowRight,
  Download
} from 'lucide-react'

const BillingSettings = ({ stripeReturn }) => {
  const { user, refreshUser } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(null)
  const [selfBillingAccepted, setSelfBillingAccepted] = useState(!!user?.selfBillingAgreedAt)

  // Billing form data
  const [billingData, setBillingData] = useState({
    btwStatus: '',
    btwNummer: '',
    kvkNummer: '',
    legalName: '',
    billingAddress: {
      street: '',
      houseNumber: '',
      postalCode: '',
      city: '',
      country: 'Nederland'
    },
    iban: '',
    accountHolderName: ''
  })

  // Stripe Connect status
  const [stripeStatus, setStripeStatus] = useState({
    connected: false,
    status: 'not_connected',
    balance: null
  })

  // Invoices
  const [invoices, setInvoices] = useState({ sent: [], received: [] })
  const [invoicesLoading, setInvoicesLoading] = useState(true)
  const [downloadingId, setDownloadingId] = useState(null)

  useEffect(() => {
    fetchBillingData()
    fetchInvoices()
    if (user?.role === 'creator') {
      fetchStripeStatus()
    }
  }, [user])

  const fetchInvoices = async () => {
    try {
      setInvoicesLoading(true)
      const res = await invoicesAPI.getMyInvoices()
      const data = res.data?.data || { sent: [], received: [] }
      setInvoices({ sent: data.sent || [], received: data.received || [] })
    } catch (err) {
      console.error('Failed to fetch invoices', err)
    } finally {
      setInvoicesLoading(false)
    }
  }

  const handleDownloadInvoice = async (invoice) => {
    try {
      setDownloadingId(invoice.id)
      const res = await invoicesAPI.downloadPdf(invoice.id)
      const blob = new Blob([res.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${invoice.invoiceNumber || 'factuur'}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
    } catch (err) {
      console.error('Failed to download invoice', err)
      setError(err.response?.data?.message || 'Factuur kon niet worden gedownload (PDF mogelijk nog niet gegenereerd).')
    } finally {
      setDownloadingId(null)
    }
  }

  const formatInvoiceDate = (d) => {
    if (!d) return ''
    try { return new Date(d).toLocaleDateString('nl-NL', { day: '2-digit', month: 'short', year: 'numeric' }) }
    catch { return '' }
  }

  const invoiceTypeLabel = (type) => ({
    sales_deposit: 'Aanbetaling',
    sales_final: 'Eindfactuur',
    purchase_self_billing: 'Inkoop (self-billing)',
    platform_fee: 'Platform fee',
  })[type] || type

  // Handle Stripe return redirect - verify actual status from server
  useEffect(() => {
    if (stripeReturn === 'success') {
      // Don't trust the URL param alone - verify with Stripe
      setSuccess('Stripe gegevens worden geverifieerd...')
      fetchStripeStatus().then(() => {
        setSuccess('Stripe onboarding voltooid! Je gegevens worden geverifieerd.')
        setTimeout(() => setSuccess(null), 6000)
      }).catch(() => {
        setError('Kon Stripe status niet verifiëren. Ververs de pagina.')
        setTimeout(() => setError(null), 6000)
      })
      refreshUser().catch(() => {})
    } else if (stripeReturn === 'refresh') {
      setError('Stripe onboarding is verlopen. Probeer het opnieuw.')
      setTimeout(() => setError(null), 6000)
    }
  }, [stripeReturn])

  const fetchBillingData = async () => {
    try {
      setLoading(true)
      const response = await billingAPI.getBilling()
      if (response.data.success) {
        const data = response.data.data
        setBillingData({
          btwStatus: data.btwStatus || '',
          btwNummer: data.btwNummer || '',
          kvkNummer: data.kvkNummer || '',
          legalName: data.legalName || '',
          billingAddress: {
            street: data.billingAddress?.street || '',
            houseNumber: data.billingAddress?.houseNumber || '',
            postalCode: data.billingAddress?.postalCode || '',
            city: data.billingAddress?.city || '',
            country: data.billingAddress?.country || 'Nederland'
          },
          iban: data.iban || '',
          accountHolderName: data.accountHolderName || ''
        })
      }
    } catch (error) {
      console.error('Error fetching billing data:', error)
      setError('Kon facturatiegegevens niet laden')
    } finally {
      setLoading(false)
    }
  }

  const fetchStripeStatus = async () => {
    try {
      const response = await stripeConnectAPI.getStatus()
      if (response.data.success) {
        setStripeStatus({
          connected: response.data.connected,
          status: response.data.status?.onboardingComplete ? 'active' :
                  response.data.connected ? 'pending' : 'not_connected',
          balance: null
        })

        // Fetch balance if connected
        if (response.data.connected && response.data.status?.onboardingComplete) {
          const balanceResponse = await stripeConnectAPI.getBalance()
          if (balanceResponse.data.success) {
            setStripeStatus(prev => ({
              ...prev,
              balance: balanceResponse.data.balance
            }))
          }
        }
      }
    } catch (error) {
      console.error('Error fetching Stripe status:', error)
    }
  }

  const handleInputChange = (field, value) => {
    // Clear errors when user types
    if (error) setError(null)
    if (field.includes('.')) {
      const [parent, child] = field.split('.')
      setBillingData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }))
    } else {
      setBillingData(prev => ({
        ...prev,
        [field]: value
      }))
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      setError(null)
      setSuccess(null)

      // Validate BTW number format (only if filled in)
      if (billingData.btwNummer && !isValidBtw(billingData.btwNummer)) {
        setError('BTW-nummer moet in formaat NL123456789B01 zijn')
        setSaving(false)
        return
      }

      // Validate KvK number format (only if filled in)
      if (billingData.kvkNummer && !/^\d{8}$/.test(billingData.kvkNummer)) {
        setError('KvK-nummer moet 8 cijfers bevatten')
        setSaving(false)
        return
      }

      // Validate postal code format (only if filled in)
      if (billingData.billingAddress.postalCode &&
          !/^\d{4}\s?[A-Za-z]{2}$/i.test(billingData.billingAddress.postalCode)) {
        setError('Postcode moet in formaat 1234AB zijn')
        setSaving(false)
        return
      }

      // For creators, only send BTW/KvK fields (Stripe handles the rest)
      const dataToSend = user?.role === 'creator'
        ? {
            btwStatus: billingData.btwStatus,
            btwNummer: billingData.btwNummer || undefined,
            kvkNummer: billingData.kvkNummer || undefined
          }
        : billingData

      const response = await billingAPI.updateBilling(dataToSend)

      if (response.data.success) {
        setSuccess('Facturatiegegevens opgeslagen!')
        await refreshUser()
        setTimeout(() => setSuccess(null), 3000)
      }
    } catch (error) {
      console.error('Error saving billing data:', error)
      const errorData = error.response?.data
      const errorMsg = errorData?.message || errorData?.messages?.join(', ') || 'Kon facturatiegegevens niet opslaan'
      setError(errorMsg)
    } finally {
      setSaving(false)
    }
  }

  const initiateStripeOnboarding = async () => {
    try {
      setSaving(true)

      // First create account if not exists
      if (!stripeStatus.connected) {
        await stripeConnectAPI.createAccount()
      }

      // Get onboarding link
      const response = await stripeConnectAPI.getOnboardingLink()
      if (response.data.success) {
        window.location.href = response.data.url
      }
    } catch (error) {
      console.error('Error initiating Stripe onboarding:', error)
      setError('Kon Stripe onboarding niet starten')
    } finally {
      setSaving(false)
    }
  }

  const openStripeDashboard = async () => {
    try {
      const response = await stripeConnectAPI.getDashboardLink()
      if (response.data.success) {
        window.open(response.data.url, '_blank')
      }
    } catch (error) {
      console.error('Error getting dashboard link:', error)
      setError('Kon Stripe dashboard niet openen')
    }
  }

  const getBtwStatusInfo = (status) => {
    switch (status) {
      case 'particulier':
        return {
          label: 'Particulier',
          description: 'Je hebt geen KvK nodig. UGC4you regelt de volledige facturatie via self-billing. Je ontvangt een uitbetalingsoverzicht en geeft je inkomsten op bij de Belastingdienst als "overige werkzaamheden".',
          color: 'text-emerald-600',
          bgColor: 'bg-emerald-50'
        }
      case 'kor':
        return {
          label: 'Kleineondernemersregeling (KOR)',
          description: 'Je hebt een KvK maar valt onder de KOR. UGC4you stelt namens jou facturen op zonder BTW (self-billing). Je hoeft zelf geen facturen te sturen.',
          color: 'text-amber-600',
          bgColor: 'bg-amber-50'
        }
      case 'btw_plichtig':
        return {
          label: 'BTW-plichtig',
          description: 'Je levert als zelfstandig ondernemer diensten aan UGC4you B.V. UGC4you stelt namens jou facturen op (self-billing) en verrekent de platformfee automatisch bij uitbetaling.',
          color: 'text-[#1D8BF8]',
          bgColor: 'bg-blue-50'
        }
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-[#3EB489]"></div>
        <p className="mt-4 text-[#475569]">Gegevens laden...</p>
      </div>
    )
  }

  // ============================================
  // CREATOR VIEW - Stripe-first flow
  // ============================================
  if (user?.role === 'creator') {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center">
          <h2 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-2 md:mb-4">
            Facturatie & Uitbetalingen
          </h2>
          <p className="text-sm md:text-base text-[#475569] max-w-2xl mx-auto">
            Stel je facturatie in via Stripe om veilig en automatisch uitbetalingen te ontvangen.
          </p>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-600">{error}</p>
          </div>
        )}

        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
            <p className="text-[#3EB489]">{success}</p>
          </div>
        )}

        {/* ==================== */}
        {/* STRIPE STATUS CARD   */}
        {/* ==================== */}
        {stripeStatus.status === 'active' ? (
          /* ACTIVE - Everything is set up */
          <div className="bg-emerald-50 rounded-2xl p-5 md:p-8 border border-emerald-200">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <CheckCircle className="w-7 h-7 md:w-8 md:h-8 text-[#3EB489]" />
              </div>

              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1">Alles ingesteld!</h3>
                <p className="text-[#475569]">
                  Je Stripe account is geverifieerd en klaar om uitbetalingen te ontvangen.
                  Uitbetalingen worden automatisch verwerkt na goedkeuring van projecten.
                </p>
                {stripeStatus.balance && (
                  <div className="mt-3 inline-flex items-center gap-2 bg-white rounded-xl px-4 py-2 border border-emerald-200">
                    <Banknote className="w-4 h-4 text-[#3EB489]" />
                    <span className="text-sm text-[#475569]">Beschikbaar saldo:</span>
                    <span className="font-bold text-[#3EB489]">
                      €{stripeStatus.balance.available?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                )}
              </div>

              <button
                onClick={openStripeDashboard}
                className="px-4 py-2.5 bg-white border border-emerald-200 rounded-xl text-sm font-medium text-[#0F172A] hover:bg-emerald-100 transition inline-flex items-center"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                Stripe Dashboard
              </button>
            </div>
          </div>
        ) : stripeStatus.status === 'pending' ? (
          /* PENDING - Onboarding started but not complete */
          <div className="bg-blue-50 rounded-2xl p-5 md:p-8 border border-blue-200">
            <div className="flex flex-col md:flex-row items-start md:items-center gap-4 md:gap-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-white rounded-2xl flex items-center justify-center flex-shrink-0 shadow-sm">
                <Clock className="w-7 h-7 md:w-8 md:h-8 text-[#1D8BF8]" />
              </div>

              <div className="flex-1">
                <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-1">Stripe Onboarding Gestart</h3>
                <p className="text-[#475569]">
                  Je gegevens worden nagelopen door Stripe. Dit kan enkele minuten tot 1-2 werkdagen duren.
                  Je ontvangt een melding zodra alles is goedgekeurd.
                </p>
                <div className="mt-3 flex flex-wrap gap-3">
                  <div className="inline-flex items-center gap-2 bg-white rounded-lg px-3 py-1.5 border border-blue-100 text-sm">
                    <div className="w-2 h-2 bg-[#3EB489] rounded-full animate-pulse"></div>
                    <span className="text-[#475569]">Verificatie loopt...</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <button
                  onClick={initiateStripeOnboarding}
                  disabled={saving}
                  className="px-4 py-2.5 bg-[#3EB489] text-white rounded-xl text-sm font-medium hover:bg-[#1876D4] transition inline-flex items-center shadow-sm"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <ExternalLink className="w-4 h-4 mr-2" />
                  )}
                  Onboarding Voltooien
                </button>
                <button
                  onClick={fetchStripeStatus}
                  className="px-4 py-2 bg-white border border-blue-200 rounded-xl text-sm font-medium text-[#475569] hover:bg-blue-100 transition inline-flex items-center"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Status Vernieuwen
                </button>
              </div>
            </div>
          </div>
        ) : (
          /* NOT CONNECTED - Need to start onboarding */
          <div className="bg-gradient-to-br from-[#F8FAFC] to-blue-50 rounded-2xl p-5 md:p-8 border border-gray-200">
            <div className="text-center max-w-lg mx-auto">
              <div className="w-16 h-16 md:w-20 md:h-20 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 md:mb-6 shadow-sm border border-gray-100">
                <CreditCard className="w-8 h-8 md:w-10 md:h-10 text-[#1D8BF8]" />
              </div>

              <h3 className="text-xl md:text-2xl font-bold text-[#0F172A] mb-2">Koppel je bankrekening via Stripe</h3>
              <p className="text-[#475569] mb-6">
                Voltooi de Stripe onboarding om je bankgegevens, adres en identiteit te verifiëren.
                Na verificatie ontvang je automatisch uitbetalingen van je UGC projecten.
              </p>

              <button
                onClick={initiateStripeOnboarding}
                disabled={saving}
                className="px-8 py-3.5 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center shadow-lg shadow-[#3EB489]/20 text-base"
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                    Opzetten...
                  </>
                ) : (
                  <>
                    <ArrowRight className="w-5 h-5 mr-2" />
                    Start Stripe Onboarding
                  </>
                )}
              </button>

              {/* Steps preview */}
              <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-3 text-left">
                <div className="bg-white rounded-xl p-3 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#3EB489] rounded-full flex items-center justify-center text-xs font-bold text-white">1</div>
                    <span className="text-sm font-semibold text-[#0F172A]">Identiteit</span>
                  </div>
                  <p className="text-xs text-[#475569]">Verifieer je identiteit en adres</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#3EB489] rounded-full flex items-center justify-center text-xs font-bold text-white">2</div>
                    <span className="text-sm font-semibold text-[#0F172A]">Bankrekening</span>
                  </div>
                  <p className="text-xs text-[#475569]">Koppel je IBAN voor uitbetalingen</p>
                </div>
                <div className="bg-white rounded-xl p-3 border border-gray-100">
                  <div className="flex items-center gap-2 mb-1">
                    <div className="w-6 h-6 bg-[#3EB489] rounded-full flex items-center justify-center text-xs font-bold text-white">3</div>
                    <span className="text-sm font-semibold text-[#0F172A]">Klaar!</span>
                  </div>
                  <p className="text-xs text-[#475569]">Ontvang automatisch uitbetalingen</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ==================== */}
        {/* BTW STATUS            */}
        {/* ==================== */}
        <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
              <Calculator className="w-5 h-5 text-[#1D8BF8]" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-[#0F172A]">BTW Status</h3>
              <p className="text-sm text-[#475569]">Selecteer je fiscale status</p>
            </div>
          </div>

          <div className="grid gap-4">
            {[
              { value: 'particulier', label: 'Particulier (geen KvK)', desc: 'Je hebt geen bedrijf. UGC4you regelt de facturatie via self-billing.' },
              { value: 'kor', label: 'KvK zonder BTW (KOR)', desc: 'Je hebt een KvK maar valt onder de Kleineondernemersregeling (geen BTW)' },
              { value: 'btw_plichtig', label: 'BTW-plichtig ondernemer', desc: 'Je hebt een KvK- en BTW-nummer en draagt 21% BTW af' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => handleInputChange('btwStatus', option.value)}
                className={`w-full p-4 rounded-xl border text-left transition ${
                  billingData.btwStatus === option.value
                    ? 'bg-emerald-50 border-[#3EB489]/50'
                    : 'bg-[#F8FAFC] border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-[#0F172A]">{option.label}</div>
                    <div className="text-sm text-[#475569]">{option.desc}</div>
                  </div>
                  <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                    billingData.btwStatus === option.value
                      ? 'border-[#3EB489] bg-[#3EB489]'
                      : 'border-gray-300'
                  }`}>
                    {billingData.btwStatus === option.value && (
                      <CheckCircle className="w-3 h-3 text-white" />
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* BTW Info Card */}
          {billingData.btwStatus && (
            <div className={`mt-4 p-4 rounded-xl ${getBtwStatusInfo(billingData.btwStatus)?.bgColor}`}>
              <div className="flex gap-3">
                <Info className={`w-5 h-5 ${getBtwStatusInfo(billingData.btwStatus)?.color} flex-shrink-0`} />
                <div>
                  <h5 className={`font-semibold ${getBtwStatusInfo(billingData.btwStatus)?.color}`}>
                    {getBtwStatusInfo(billingData.btwStatus)?.label}
                  </h5>
                  <p className="text-sm text-[#475569] mt-1">
                    {getBtwStatusInfo(billingData.btwStatus)?.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* BTW & KvK numbers — shown based on fiscal status */}
          {billingData.btwStatus === 'btw_plichtig' && (
            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  BTW-nummer
                </label>
                <input
                  type="text"
                  value={billingData.btwNummer}
                  onChange={(e) => handleInputChange('btwNummer', e.target.value.toUpperCase())}
                  className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
                  placeholder="NL123456789B01"
                />
                <p className="text-xs text-[#94a3b8] mt-1">Formaat: NL + 9 cijfers + B + 2 cijfers</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#0F172A] mb-2">
                  KvK-nummer
                </label>
                <input
                  type="text"
                  value={billingData.kvkNummer}
                  onChange={(e) => handleInputChange('kvkNummer', e.target.value.replace(/\D/g, '').slice(0, 8))}
                  className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
                  placeholder="12345678"
                  maxLength={8}
                />
              </div>
            </div>
          )}

          {/* KvK only for KOR */}
          {billingData.btwStatus === 'kor' && (
            <div className="mt-4">
              <label className="block text-sm font-medium text-[#0F172A] mb-2">
                KvK-nummer
              </label>
              <input
                type="text"
                value={billingData.kvkNummer}
                onChange={(e) => handleInputChange('kvkNummer', e.target.value.replace(/\D/g, '').slice(0, 8))}
                className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
                placeholder="12345678"
                maxLength={8}
              />
            </div>
          )}
        </div>

        {/* ==================== */}
        {/* SELF-BILLING          */}
        {/* ==================== */}
        {!selfBillingAccepted ? (
          <div className="bg-amber-50 rounded-2xl p-4 md:p-6 border border-amber-200">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h5 className="font-semibold text-amber-800 mb-2">Self-billing overeenkomst</h5>
                <p className="text-sm text-[#475569] mb-4">
                  Om facturen te kunnen genereren en uitbetalingen te ontvangen, moet je de self-billing overeenkomst accepteren.
                  UGC4you stelt namens jou inkoopfacturen op. Je hoeft zelf geen facturen te sturen.
                </p>
                <div className="bg-white rounded-xl p-4 border border-amber-200 mb-4">
                  <p className="text-sm text-[#475569]">
                    <strong>Self-billing overeenkomst:</strong> Ik ga ermee akkoord dat UGC4you B.V. namens mij inkoopfacturen
                    (self-billing) opstelt voor diensten die ik via het platform lever. Ik maak voor deze leveringen zelf geen factuur op.
                    Ik meld wijzigingen in mijn BTW-status direct aan UGC4you.
                  </p>
                </div>
                <button
                  onClick={async () => {
                    try {
                      setSaving(true)
                      await billingAPI.updateBilling({ acceptSelfBilling: true })
                      setSelfBillingAccepted(true)
                      refreshUser().catch(() => {})
                      setSuccess('Self-billing overeenkomst geaccepteerd!')
                      setTimeout(() => setSuccess(null), 3000)
                    } catch (err) {
                      setError('Kon self-billing overeenkomst niet opslaan')
                    } finally {
                      setSaving(false)
                    }
                  }}
                  disabled={saving}
                  className="px-6 py-2.5 bg-amber-600 text-white rounded-xl font-medium hover:bg-amber-700 transition inline-flex items-center"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Self-billing overeenkomst accepteren
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0" />
              <div>
                <h5 className="font-semibold text-[#3EB489]">Self-billing overeenkomst geaccepteerd</h5>
                <p className="text-xs text-[#475569] mt-1">
                  Geaccepteerd op {new Date(user?.selfBillingAgreedAt || new Date()).toLocaleDateString('nl-NL')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* ==================== */}
        {/* PAYMENT INFO          */}
        {/* ==================== */}
        <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
            <div>
              <h5 className="font-semibold text-[#3EB489] mb-2">Hoe worden betalingen berekend?</h5>
              <div className="text-sm text-[#475569] space-y-2">
                <p><strong className="text-[#0F172A]">UGC4you koopt jouw diensten in</strong> en verkoopt deze door aan het bedrijf. Je levert als zelfstandig ondernemer aan UGC4you B.V.</p>
                <p><strong className="text-[#0F172A]">Uitbetaling:</strong> Projectbedrag + 21% BTW, minus platformfee (15% + BTW). De platformfee wordt automatisch verrekend.</p>
                <p><strong className="text-[#0F172A]">Self-billing:</strong> UGC4you stelt namens jou een inkoopfactuur op. Je hoeft zelf geen factuur te sturen.</p>
                <p className="text-[#94a3b8] text-xs mt-2">
                  Voorbeeld bij €500 budget: je ontvangt €605 (incl. BTW) minus €90,75 platformfee = €514,25 uitbetaling.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Voordelen */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
            <ShieldCheck className="w-5 h-5 text-[#3EB489] mx-auto mb-1" />
            <p className="text-xs text-[#475569]">Bank-level beveiliging</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
            <Zap className="w-5 h-5 text-amber-500 mx-auto mb-1" />
            <p className="text-xs text-[#475569]">48u uitbetaling</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
            <Banknote className="w-5 h-5 text-emerald-500 mx-auto mb-1" />
            <p className="text-xs text-[#475569]">85% naar jou</p>
          </div>
          <div className="bg-white rounded-xl p-3 border border-gray-100 text-center">
            <Building2 className="w-5 h-5 text-[#1D8BF8] mx-auto mb-1" />
            <p className="text-xs text-[#475569]">Alle NL banken</p>
          </div>
        </div>

        {/* Save Button for BTW data */}
        <div className="flex flex-col items-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-8 py-3 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center shadow-lg shadow-[#3EB489]/20"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Opslaan...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Gegevens Opslaan
              </>
            )}
          </button>
        </div>
      </div>
    )
  }

  // ============================================
  // BUSINESS/AGENCY VIEW - Full form (unchanged)
  // ============================================
  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-2xl md:text-3xl font-black text-[#0F172A] mb-2 md:mb-4">
          Facturatiegegevens
        </h2>
        <p className="text-sm md:text-base text-[#475569] max-w-2xl mx-auto">
          Beheer je bedrijfsgegevens en factuuradres.
        </p>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
          <p className="text-red-600">{error}</p>
        </div>
      )}

      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-[#3EB489] flex-shrink-0 mt-0.5" />
          <p className="text-[#3EB489]">{success}</p>
        </div>
      )}

      {/* My Invoices */}
      <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#1D8BF8]" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0F172A]">Mijn facturen</h3>
            <p className="text-sm text-[#475569]">Download facturen die je hebt ontvangen of verstuurd.</p>
          </div>
        </div>

        {invoicesLoading ? (
          <div className="flex items-center gap-2 text-sm text-[#475569] py-4">
            <RefreshCw className="w-4 h-4 animate-spin" />
            Facturen laden...
          </div>
        ) : (invoices.received.length === 0 && invoices.sent.length === 0) ? (
          <div className="py-6 text-center text-sm text-[#475569] bg-[#F8FAFC] rounded-xl border border-dashed border-gray-200">
            Je hebt nog geen facturen. Zodra een opdracht wordt betaald, vind je hier de bijbehorende facturen.
          </div>
        ) : (
          <div className="space-y-6">
            {invoices.received.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#0F172A] mb-3">Ontvangen ({invoices.received.length})</h4>
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                  {invoices.received.map((inv) => (
                    /* Mobiel gestapeld: info bovenaan, daaronder bedrag + PDF in één sub-rij.
                       Vanaf sm valt de sub-rij weg (contents) en staan info, bedrag en PDF naast elkaar zoals voorheen. */
                    <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 hover:bg-[#F8FAFC] transition">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm text-[#0F172A] break-all">{inv.invoiceNumber}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1D8BF8] whitespace-nowrap">
                            {invoiceTypeLabel(inv.type)}
                          </span>
                          {inv.status === 'paid' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 whitespace-nowrap">Betaald</span>
                          )}
                        </div>
                        <div className="text-xs text-[#475569] truncate">
                          {inv.sender} · {formatInvoiceDate(inv.invoiceDate)}
                          {inv.project?.title && <> · {inv.project.title}</>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:contents">
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-semibold text-[#0F172A] whitespace-nowrap">€{inv.total?.toFixed(2)}</div>
                        </div>
                        <button
                          onClick={() => handleDownloadInvoice(inv)}
                          disabled={downloadingId === inv.id}
                          className="flex items-center gap-1.5 px-3 py-2 min-h-[40px] text-sm font-medium text-[#1D8BF8] hover:bg-blue-50 rounded-lg transition disabled:opacity-60 flex-shrink-0"
                        >
                          {downloadingId === inv.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {invoices.sent.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-[#0F172A] mb-3">Verstuurd ({invoices.sent.length})</h4>
                <div className="divide-y divide-gray-100 border border-gray-100 rounded-xl overflow-hidden">
                  {invoices.sent.map((inv) => (
                    <div key={inv.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 p-3 hover:bg-[#F8FAFC] transition">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2 mb-0.5">
                          <span className="font-semibold text-sm text-[#0F172A] break-all">{inv.invoiceNumber}</span>
                          <span className="text-xs px-2 py-0.5 rounded-full bg-blue-50 text-[#1D8BF8] whitespace-nowrap">
                            {invoiceTypeLabel(inv.type)}
                          </span>
                          {inv.status === 'paid' && (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 whitespace-nowrap">Betaald</span>
                          )}
                        </div>
                        <div className="text-xs text-[#475569] truncate">
                          Naar {inv.recipient} · {formatInvoiceDate(inv.invoiceDate)}
                          {inv.project?.title && <> · {inv.project.title}</>}
                        </div>
                      </div>
                      <div className="flex items-center justify-between gap-3 sm:contents">
                        <div className="text-right flex-shrink-0">
                          <div className="text-sm font-semibold text-[#0F172A] whitespace-nowrap">€{inv.total?.toFixed(2)}</div>
                        </div>
                        <button
                          onClick={() => handleDownloadInvoice(inv)}
                          disabled={downloadingId === inv.id}
                          className="flex items-center gap-1.5 px-3 py-2 min-h-[40px] text-sm font-medium text-[#1D8BF8] hover:bg-blue-50 rounded-lg transition disabled:opacity-60 flex-shrink-0"
                        >
                          {downloadingId === inv.id ? (
                            <RefreshCw className="w-4 h-4 animate-spin" />
                          ) : (
                            <Download className="w-4 h-4" />
                          )}
                          PDF
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Business Details */}
      <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-purple-50 rounded-xl flex items-center justify-center">
            <Building2 className="w-5 h-5 text-purple-500" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0F172A]">Bedrijfsgegevens</h3>
            <p className="text-sm text-[#475569]">Voor op je facturen</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              Officiële naam voor op facturen
            </label>
            <input
              type="text"
              value={billingData.legalName}
              onChange={(e) => handleInputChange('legalName', e.target.value)}
              className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
              placeholder="Je volledige naam of bedrijfsnaam"
            />
            {/* Heette eerder "Officiële naam / Bedrijfsnaam", waardoor mensen dachten
                dat ze hier hun bedrijfsnaam invulden. Dit is legalName; de bedrijfsnaam
                die voor het plaatsen van een brief telt is companyName, op de Profiel-tab. */}
            <p className="mt-2 text-xs text-[#64748B]">
              Alleen voor je facturen. Wordt automatisch opgehaald uit het btw-/KVK-register zodra je je btw- of KvK-nummer opslaat; je kunt de naam hier zelf aanpassen. Je bedrijfsnaam wijzig je onder Profiel.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              KvK-nummer
              <span className="text-[#1D8BF8] ml-1">(nodig zodra je een voorstel accepteert en betaalt)</span>
            </label>
            <input
              type="text"
              value={billingData.kvkNummer}
              onChange={(e) => handleInputChange('kvkNummer', e.target.value.replace(/\D/g, '').slice(0, 8))}
              className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
              placeholder="12345678"
              maxLength={8}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">
              BTW-nummer
              <span className="text-[#94a3b8] ml-1">(optioneel)</span>
            </label>
            <input
              type="text"
              value={billingData.btwNummer}
              onChange={(e) => handleInputChange('btwNummer', e.target.value.toUpperCase())}
              className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
              placeholder="NL123456789B01"
            />
          </div>
        </div>
      </div>

      {/* Billing Address */}
      <div className="bg-white rounded-2xl p-4 md:p-6 border border-gray-100 shadow-sm">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
            <FileText className="w-5 h-5 text-[#3EB489]" />
          </div>
          <div>
            <h3 className="text-xl font-bold text-[#0F172A]">Factuuradres</h3>
            <p className="text-sm text-[#475569]">Dit adres komt op je facturen</p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Straatnaam</label>
              <input
                type="text"
                value={billingData.billingAddress.street}
                onChange={(e) => handleInputChange('billingAddress.street', e.target.value)}
                className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
                placeholder="Straatnaam"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#0F172A] mb-2">Huisnummer</label>
              <input
                type="text"
                value={billingData.billingAddress.houseNumber}
                onChange={(e) => handleInputChange('billingAddress.houseNumber', e.target.value)}
                className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
                placeholder="123a"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">Postcode</label>
            <input
              type="text"
              value={billingData.billingAddress.postalCode}
              onChange={(e) => handleInputChange('billingAddress.postalCode', e.target.value.toUpperCase())}
              className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
              placeholder="1234AB"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#0F172A] mb-2">Plaats</label>
            <input
              type="text"
              value={billingData.billingAddress.city}
              onChange={(e) => handleInputChange('billingAddress.city', e.target.value)}
              className="w-full bg-[#F8FAFC] border border-gray-200 rounded-xl px-4 py-3 focus:ring-2 focus:ring-[#3EB489]/30 focus:border-[#3EB489] text-[#0F172A] placeholder-[#94a3b8]"
              placeholder="Amsterdam"
            />
          </div>
        </div>
      </div>

      {/* Save Button with inline feedback */}
      <div className="flex flex-col items-end gap-3">
        {success && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
            <CheckCircle className="w-4 h-4 text-[#3EB489] flex-shrink-0" />
            <p className="text-sm font-medium text-[#3EB489]">{success}</p>
          </div>
        )}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-2.5 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <p className="text-sm font-medium text-red-600">{error}</p>
          </div>
        )}
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-8 py-3 bg-[#3EB489] text-white rounded-2xl font-semibold hover:bg-[#359E78] transition inline-flex items-center shadow-lg shadow-[#3EB489]/20"
        >
          {saving ? (
            <>
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              Opslaan...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Gegevens Opslaan
            </>
          )}
        </button>
      </div>
    </div>
  )
}

export default BillingSettings
